/**
 * amplitudeHealthSignal.tsx — сигнал «подключение Amplitude сохранено или
 * проверено» от строки настроек к списку «Требует внимания» (WEB-2413 п.9;
 * WEB-3782 — слушатель теперь `ProjectAttentionProvider`).
 *
 * ЗАЧЕМ. Поломка доставки видна вне настроек — раньше красной плашкой каркаса
 * (`AnalyticsDeliveryBanner`, снята WEB-3782), теперь строкой блока «Требует
 * внимания» и числом у «Главной» в меню. Список спрашивался один раз на
 * проект: человек чинил ключ, проверка проходила, а поломка висела до
 * перезагрузки. Строка Amplitude (`AmplitudeRow`) и поставщик списка — соседи
 * в разных ветках дерева, поэтому связь — через контекст, который держит
 * каркас (`DashboardLayout`): строка увеличивает номер, поставщик перечитывает
 * список на новом номере.
 *
 * Не шина и не глобальная переменная: состояние живёт в поставщике и
 * пропадает вместе с ним (память модуля переживала бы тесты — шрам 22.09).
 * Без поставщика (отдельный экран, тест) сигнал — пустое действие, номер 0.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface AmplitudeHealthSignal {
  version: number;
  changed: () => void;
}

const AmplitudeHealthSignalContext =
  createContext<AmplitudeHealthSignal | null>(null);

const noop = () => {};

export function AmplitudeHealthSignalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [version, setVersion] = useState(0);
  const changed = useCallback(() => setVersion((value) => value + 1), []);
  const value = useMemo(() => ({ version, changed }), [version, changed]);
  return (
    <AmplitudeHealthSignalContext.Provider value={value}>
      {children}
    </AmplitudeHealthSignalContext.Provider>
  );
}

/** Номер последнего изменения подключения — зависимость перечитывания здоровья. */
export function useAmplitudeHealthVersion(): number {
  return useContext(AmplitudeHealthSignalContext)?.version ?? 0;
}

/** Сообщить: подключение сохранено или проверено — здоровье могло измениться. */
export function useAmplitudeHealthChanged(): () => void {
  return useContext(AmplitudeHealthSignalContext)?.changed ?? noop;
}
